import { ClipLoader } from 'react-spinners';
import '../../styles/editcase/FactsPanel.css';

/**
 * Full-width fact groups with verification controls.
 */
export default function FactsPanel({
    groupedFacts,
    collapsedGroups,
    onToggleGroup,
    extractionProgress,
    editableAnswers,
    expandedCitations,
    onToggleCitation,
    onFactVerification,
    onAnswerEdit,
    fileCount,
}) {
    return (
        <div className="facts-panel">
            {extractionProgress && (
                <div className="extraction-progress">
                    <ClipLoader size={18} color="#856404" />
                    <span>
                        Extracting medical information: {extractionProgress.completed} of {extractionProgress.total} fields
                    </span>
                </div>
            )}

            {groupedFacts.length === 0 && (
                <p className="facts-panel-empty">Upload a document to view structured patient information.</p>
            )}

            <div className="structured-info-container">
                {groupedFacts.map((group) => (
                    <div key={group.name} className="fact-group">
                        <div
                            className="fact-group-header"
                            onClick={() => onToggleGroup(group.name)}
                        >
                            <div className="fact-group-header-left">
                                <span className={`fact-group-chevron ${collapsedGroups[group.name] ? 'collapsed' : ''}`}>&#9654;</span>
                                <span className="fact-group-title">{group.name}</span>
                            </div>
                            <span className={`fact-group-progress ${group.verifiedCount === group.totalCount ? 'complete' : ''}`}>
                                {group.verifiedCount}/{group.totalCount} verified
                            </span>
                        </div>
                        {!collapsedGroups[group.name] && (
                            <div className="fact-group-body">
                                {group.facts.map(([key, value]) => (
                                    <EditFactItem
                                        key={key}
                                        fieldKey={key}
                                        value={value}
                                        editableValue={editableAnswers?.[key]}
                                        isCitationExpanded={expandedCitations?.[key]}
                                        onToggleCitation={onToggleCitation}
                                        onFactVerification={onFactVerification}
                                        onAnswerEdit={onAnswerEdit}
                                        fileCount={fileCount}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function EditFactItem({ fieldKey, value, editableValue, isCitationExpanded, onToggleCitation, onFactVerification, onAnswerEdit, fileCount }) {
    const SENTINEL = 'Information not found';
    const pageKnown = value.page && value.page !== SENTINEL;
    const documentKnown = value.document && value.document !== SENTINEL;
    const isMultiDoc = (fileCount ?? 0) >= 2;

    let badgeText = null;
    if (isMultiDoc && documentKnown && pageKnown) {
        badgeText = `${value.document} · p. ${value.page}`;
    } else if (isMultiDoc && documentKnown) {
        badgeText = value.document;
    } else if (pageKnown) {
        badgeText = `Page ${value.page}`;
    }

    return (
        <div className="info-item">
            <div className="info-header">
                <h3>{fieldKey}</h3>
                <div className="verification-controls">
                    <button
                        className={`verify-button correct ${value.verified === true ? 'active' : ''}`}
                        onClick={(e) => onFactVerification(fieldKey, !value.verified, e)}
                        type="button"
                    >
                        <span className="icon">&#10003;</span>
                        {value.verified ? 'Uncheck' : 'Correct'}
                    </button>
                    <button
                        className={`verify-button modify ${value.verified === false ? 'active' : ''}`}
                        onClick={(e) => onFactVerification(fieldKey, false, e)}
                        type="button"
                    >
                        <span className="icon">&#9998;</span>
                        Modify
                    </button>
                </div>
            </div>
            {value.verified === false ? (
                <textarea
                    className="info-answer editable"
                    value={editableValue ?? value.answer}
                    onChange={(e) => onAnswerEdit(fieldKey, e.target.value)}
                    rows={3}
                />
            ) : (
                <p className="info-answer">{value.answer}</p>
            )}
            {value.citation && value.citation !== SENTINEL && (
                <div className="citation-toggle-container">
                    <button
                        className="citation-toggle"
                        onClick={() => onToggleCitation(fieldKey)}
                        type="button"
                    >
                        {isCitationExpanded ? 'Hide citation' : 'View citation'}
                        {badgeText && (
                            <span className="citation-page-badge">{badgeText}</span>
                        )}
                    </button>
                    {isCitationExpanded && (
                        <blockquote className="citation-text">{value.citation}</blockquote>
                    )}
                </div>
            )}
        </div>
    );
}
